import { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useChartTheme } from '../../../../utils/chartTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Two counts of the same things on one axis: how many distinct people, and
// how many batch seats they take up. The gap between the pair is the whole
// point of this report -- a department where the second bar runs well past
// the first is one where students keep coming back for another course.
//
// Series slots 1 and 2 of the chart theme (utils/chartTheme): slot 1 is the
// palette's accent, slot 2 the first fixed hue that stays distinguishable
// from it. The slot follows the measure, never its position on screen.
const SERIES = [
  { key: 'studentCount', label: 'Students', slot: 0 },
  { key: 'batchEnrollments', label: 'Batch enrolments', slot: 1 },
];

// Course is the long tail -- 78 of them against 14 departments and 4
// branches -- so every dimension is cut to the same top slice and the
// heading says so rather than implying the chart is the whole set.
const TOP_N = 10;

const DIMENSIONS = [
  { key: 'byDepartment', label: 'Department', name: 'departmentName' },
  { key: 'byCourse', label: 'Course', name: 'courseName' },
  { key: 'byBranch', label: 'Branch', name: 'branchName' },
];

const BatchMixChart = ({ summary }) => {
  const theme = useChartTheme();
  const [dim, setDim] = useState('byDepartment');

  const active = DIMENSIONS.find((d) => d.key === dim) || DIMENSIONS[0];
  const all = summary?.[active.key] || [];

  // The API already sorts each rollup by studentCount descending.
  const rows = useMemo(() => all.slice(0, TOP_N), [all]);

  const data = useMemo(
    () => ({
      labels: rows.map((r) => r[active.name] || 'Unknown'),
      datasets: SERIES.map((s) => ({
        label: s.label,
        data: rows.map((r) => r[s.key] || 0),
        backgroundColor: theme.series[s.slot],
        hoverBackgroundColor: theme.series[s.slot],
        // Rounded at the value, square at the baseline it grows from.
        borderRadius: 4,
        borderSkipped: 'start',
        barThickness: 11,
      })),
    }),
    [rows, active.name, theme],
  );

  const options = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      // A 2px gap between the pair, so the two bars read as one row's
      // reading rather than as a single striped block.
      datasets: { bar: { categoryPercentage: 0.72, barPercentage: 0.86 } },
      plugins: {
        // Two series, so a legend -- identity is never colour-matching
        // alone. The swatch carries the hue; the words stay in ink.
        legend: {
          display: true,
          position: 'top',
          align: 'end',
          labels: theme.legendLabels(),
        },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) => {
              const r = rows[ctx.dataIndex] || {};
              const students = r.studentCount || 0;
              const seats = r.batchEnrollments || 0;
              const per = students ? (seats / students).toFixed(2) : '0';
              return ctx.datasetIndex === 0
                ? ` ${students.toLocaleString()} students`
                : [
                    ` ${seats.toLocaleString()} batch enrolments`,
                    ` ${per} per student`,
                  ];
            },
          },
        },
      },
      scales: {
        x: theme.valueAxis({
          beginAtZero: true,
          ticks: {
            precision: 0,
            callback: (v) => (v >= 1000 ? `${v / 1000}k` : v),
          },
        }),
        y: theme.categoryAxis({
          ticks: {
            // "Softskill (Basic + Intermediate )" is a real course name;
            // trim it here rather than let it eat half the panel.
            callback(value) {
              const label = this.getLabelForValue(value);
              return label.length > 26 ? `${label.slice(0, 26)}…` : label;
            },
          },
        }),
      },
    }),
    [rows, theme],
  );

  return (
    <section className="br-chart" aria-label="Where the students are">
      <header className="br-chart-head">
        <div>
          <h5 className="br-chart-title">Where the students are</h5>
          <p className="br-chart-sub">
            Distinct students against the batch seats they take up.
            {all.length > TOP_N && ` Top ${TOP_N} of ${all.length}.`}
          </p>
        </div>

        <div className="br-seg" role="group" aria-label="Group the chart by">
          {DIMENSIONS.map((d) => (
            <button
              type="button"
              key={d.key}
              aria-pressed={dim === d.key}
              className={`br-seg-btn${dim === d.key ? ' is-on' : ''}`}
              onClick={() => setDim(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>
      </header>

      {rows.length === 0 ? (
        <p className="br-chart-state">Nothing to chart yet.</p>
      ) : (
        <div
          className="br-chart-plot"
          style={{ height: `${Math.max(220, rows.length * 34 + 56)}px` }}
        >
          <Bar data={data} options={options} />
        </div>
      )}
    </section>
  );
};

export default BatchMixChart;
